import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import MetricsPanel from "./components/MetricsPanel";
import FilterBar from "./components/FilterBar";
import OrganizationTable from "./components/OrganizationTable";
import ConfirmationModal from "./components/ConfirmationModal";
import DetailModal from "./components/DetailModal";
import ToastNotification from "./components/ToastNotification";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import SkeletonLoader, { SkeletonKpi, SkeletonTableRows } from '../../components/SkeletonLoader';

let toastIdCounter = 0;

const AdministrativeDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({ search: "", status: "ALL", dateFrom: "", dateTo: "" });
  const [modal, setModal] = useState({ type: null, org: null, loading: false });
  const [detailModal, setDetailModal] = useState({ open: false, org: null });
  const [toasts, setToasts] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const addToast = useCallback((type, title, message) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, type, title, message }]);
    setTimeout(() => setToasts((prev) => prev?.filter((t) => t?.id !== id)), 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev?.filter((t) => t?.id !== id));
  }, []);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('organizations')?.select('*, profiles(id, full_name, email, role, status)')?.order('created_at', { ascending: false });

      if (error) throw error;

      const mapped = (data || [])?.map(org => ({
        id: org?.id,
        nit: org?.nit,
        businessName: org?.razon_social,
        email: org?.email,
        phone: org?.phone || '',
        city: org?.city || '',
        address: org?.address || '',
        contactName: org?.profiles?.[0]?.full_name || '',
        registrationDate: new Date(org.created_at)?.toLocaleDateString('es-CO'),
        status: org?.status,
        _raw: org,
      }));

      setOrganizations(mapped);
      setPendingCount(mapped?.filter(o => o?.status === 'PENDING')?.length);
    } catch (err) {
      addToast('error', 'Error', 'No se pudieron cargar las organizaciones.');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchOrganizations();

    // Real-time subscription on profiles for pending count
    const channel = supabase?.channel('profiles_changes')?.on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        fetchOrganizations();
      })?.subscribe();

    return () => supabase?.removeChannel(channel);
  }, [fetchOrganizations]);

  const filteredOrgs = useMemo(() => {
    return organizations?.filter((org) => {
      const matchSearch =
        !filters?.search ||
        org?.businessName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        org?.nit?.includes(filters?.search) ||
        org?.email?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchStatus = filters?.status === "ALL" || org?.status === filters?.status;
      return matchSearch && matchStatus;
    });
  }, [organizations, filters]);

  const metrics = useMemo(() => ({
    pending: organizations?.filter((o) => o?.status === "PENDING")?.length,
    recentActivations: organizations?.filter((o) => o?.status === "ACTIVE")?.length,
    active: organizations?.filter((o) => o?.status === "ACTIVE")?.length,
    alerts: organizations?.filter((o) => o?.status === "SUSPENDED")?.length,
  }), [organizations]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setSelectedIds([]);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => prev?.includes(id) ? prev?.filter((x) => x !== id) : [...prev, id]);
  };

  const handleToggleSelectAll = () => {
    if (filteredOrgs?.every((o) => selectedIds?.includes(o?.id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredOrgs?.map((o) => o?.id));
    }
  };

  const openModal = (type, org) => setModal({ type, org, loading: false });
  const closeModal = () => setModal({ type: null, org: null, loading: false });

  const handleConfirm = async () => {
    setModal((prev) => ({ ...prev, loading: true }));
    const { type, org } = modal;
    const statusMap = { approve: "ACTIVE", reject: "REJECTED", suspend: "SUSPENDED", reactivate: "ACTIVE" };
    const newStatus = statusMap?.[type];

    try {
      // Update organization status
      const { error: orgError } = await supabase?.from('organizations')?.update({ status: newStatus })?.eq('id', org?.id);

      if (orgError) throw orgError;

      // Update associated profiles status
      const profileStatus = newStatus === 'ACTIVE' ? 'ACTIVE' : newStatus === 'REJECTED' ? 'REJECTED' : 'SUSPENDED';
      await supabase?.from('profiles')?.update({ status: profileStatus })?.eq('organization_id', org?.id);

      // Insert audit log
      const actionMap = { approve: 'USER_ACTIVATED', reject: 'USER_REJECTED', suspend: 'USER_SUSPENDED', reactivate: 'USER_ACTIVATED' };
      await supabase?.from('audit_logs')?.insert({
        actor_id: userProfile?.id,
        action: actionMap?.[type],
        target_type: 'organization',
        target_id: org?.id,
        metadata: { organization: org?.businessName, nit: org?.nit, new_status: newStatus },
      });

      const messages = {
        approve: { title: "Organizaci\u00f3n Activada", msg: `${org?.businessName} ha sido activada.` },
        reject: { title: "Solicitud Rechazada", msg: `La solicitud de ${org?.businessName} fue rechazada.` },
        suspend: { title: "Organizaci\u00f3n Suspendida", msg: `${org?.businessName} ha sido suspendida.` },
        reactivate: { title: "Organizaci\u00f3n Reactivada", msg: `${org?.businessName} ha sido reactivada.` },
      };
      const toastType = type === "reject" ? "error" : type === "suspend" ? "warning" : "success";
      addToast(toastType, messages?.[type]?.title, messages?.[type]?.msg);

      await fetchOrganizations();
    } catch (err) {
      addToast('error', 'Error', 'No se pudo completar la acción. Intenta de nuevo.');
    } finally {
      closeModal();
    }
  };

  const handleBulkApprove = async () => {
    const count = selectedIds?.length;
    try {
      await supabase?.from('organizations')?.update({ status: 'ACTIVE' })?.in('id', selectedIds);
      for (const id of selectedIds) {
        await supabase?.from('profiles')?.update({ status: 'ACTIVE' })?.eq('organization_id', id);
      }
      setSelectedIds([]);
      addToast("success", "Aprobaci\u00f3n Masiva", `${count} organizaci\u00f3n(es) aprobadas.`);
      await fetchOrganizations();
    } catch (err) {
      addToast('error', 'Error', 'No se pudo completar la aprobaci\u00f3n masiva.');
    }
  };

  const handleBulkReject = async () => {
    const count = selectedIds?.length;
    try {
      await supabase?.from('organizations')?.update({ status: 'REJECTED' })?.in('id', selectedIds);
      for (const id of selectedIds) {
        await supabase?.from('profiles')?.update({ status: 'REJECTED' })?.eq('organization_id', id);
      }
      setSelectedIds([]);
      addToast("warning", "Rechazo Masivo", `${count} organizaci\u00f3n(es) rechazadas.`);
      await fetchOrganizations();
    } catch (err) {
      addToast('error', 'Error', 'No se pudo completar el rechazo masivo.');
    }
  };

  const handleExport = () => {
    addToast("info", "Exportando", "El reporte se est\u00e1 generando.");
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const sidebarWidth = sidebarCollapsed ? 60 : 240;
  const currentUser = {
    name: userProfile?.full_name || 'Administrador',
    role: userProfile?.role || 'ADMIN',
    organization: userProfile?.organizations?.razon_social || 'Update-vital-Ftech',
  };
  const notifications = { pendingApprovals: pendingCount, pendingUsers: 0, pendingUpdates: 0, availableUpdates: 0 };

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: 'var(--color-background)' }}>
        <RoleBasedSidebar
          user={userProfile ? { name: userProfile?.full_name, role: userProfile?.role, organization: userProfile?.organizations?.razon_social } : { name: '', role: 'ADMIN', organization: '' }}
          notifications={{ pendingApprovals: 0, pendingUsers: 0, pendingUpdates: 0, availableUpdates: 0 }}
          isCollapsed={sidebarCollapsed}
          onCollapsedChange={setSidebarCollapsed}
        />
        <div style={{ flex: 1, marginLeft: sidebarCollapsed ? '60px' : '240px', padding: '24px', transition: 'margin-left 0.2s ease' }}>
          <div style={{ marginBottom: '24px' }}>
            <SkeletonKpi count={4} />
          </div>
          <div style={{ backgroundColor: 'var(--color-card)', borderRadius: 'var(--radius-lg)', padding: '20px', boxShadow: 'var(--shadow-md)' }}>
            <SkeletonTableRows rows={7} cols={6} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--color-background)' }}>
      <RoleBasedSidebar
        user={{
          name: userProfile?.full_name || 'Administrador',
          role: userProfile?.role || 'ADMIN',
          organization: 'Update-vital-Ftech',
        }}
        notifications={{ pendingApprovals: pendingCount }}
        isCollapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
        onLogout={handleLogout}
      />
      <main className="flex-1 overflow-y-auto">
        {/* Header — Vital branded */}
        <div className="sticky top-0 z-10 px-6 py-4 flex items-center justify-between" style={{ background: 'var(--color-card)', borderBottom: '2px solid var(--color-primary)', boxShadow: '0 2px 8px rgba(0,188,212,0.1)' }}>
          <div className="flex items-center gap-3 pl-10 lg:pl-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#00BCD4' }}>
              <Icon name="LayoutDashboard" size={18} color="#fff" />
            </div>
            <div>
              <h1 className="text-heading-4 font-heading font-bold leading-tight" style={{ color: 'var(--color-foreground)' }}>Panel de Control</h1>
              <p className="text-xs font-caption" style={{ color: 'var(--color-primary)' }}>Gestión de organizaciones y usuarios</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds?.length > 0 && (
              <>
                <Button size="sm" variant="default" onClick={handleBulkApprove} style={{ backgroundColor: '#00BCD4', borderColor: '#00BCD4' }}>
                  <Icon name="CheckCircle" size={14} color="#fff" className="mr-1" />
                  Aprobar ({selectedIds?.length})
                </Button>
                <Button size="sm" variant="destructive" onClick={handleBulkReject}>
                  <Icon name="XCircle" size={14} color="#fff" className="mr-1" />
                  Rechazar ({selectedIds?.length})
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={handleExport} style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}>
              <Icon name="Download" size={14} color="currentColor" className="mr-1" />
              Exportar
            </Button>
          </div>
        </div>

        <div className="px-4 md:px-6 lg:px-8 py-6">
          <>
            <MetricsPanel metrics={metrics} />
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading font-bold text-base md:text-lg" style={{ color: "var(--color-foreground)" }}>Organizaciones Registradas</h2>
                <p className="text-xs font-caption mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>{filteredOrgs?.length} resultado(s) encontrado(s)</p>
              </div>
            </div>
            <FilterBar filters={filters} onFilterChange={handleFilterChange} onExport={handleExport} selectedCount={selectedIds?.length} onBulkApprove={handleBulkApprove} onBulkReject={handleBulkReject} />
            <OrganizationTable organizations={filteredOrgs} selectedIds={selectedIds} onToggleSelect={handleToggleSelect} onToggleSelectAll={handleToggleSelectAll} onApprove={(org) => openModal("approve", org)} onReject={(org) => openModal("reject", org)} onSuspend={(org) => openModal("suspend", org)} onReactivate={(org) => openModal("reactivate", org)} onViewDetails={(org) => setDetailModal({ open: true, org })} />

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Gesti\u00f3n de Usuarios", icon: "Users", path: "/user-management" },
                { label: "Actualizaciones", icon: "RefreshCw", path: "/update-management" },
                { label: "Configuraci\u00f3n", icon: "Settings", path: "/system-configuration" },
                { label: "Anal\u00edticas", icon: "BarChart3", path: "/analytics-dashboard" },
              ]?.map((link) => (
                <button key={link?.path} onClick={() => navigate(link?.path)} className="flex items-center gap-2 p-3 rounded-xl border text-left transition-all hover:shadow-md" style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-card)", color: "var(--color-foreground)" }}>
                  <Icon name={link?.icon} size={16} color="var(--color-primary)" />
                  <span className="text-xs font-caption font-medium line-clamp-1">{link?.label}</span>
                </button>
              ))}
            </div>
          </>
        </div>
      </main>

      <ConfirmationModal isOpen={!!modal?.type} action={modal?.type} organization={modal?.org} onConfirm={handleConfirm} onCancel={closeModal} loading={modal?.loading} />
      <DetailModal isOpen={detailModal?.open} organization={detailModal?.org} onClose={() => setDetailModal({ open: false, org: null })} onApprove={(org) => { setDetailModal({ open: false, org: null }); openModal("approve", org); }} onReject={(org) => { setDetailModal({ open: false, org: null }); openModal("reject", org); }} onSuspend={(org) => { setDetailModal({ open: false, org: null }); openModal("suspend", org); }} onReactivate={(org) => { setDetailModal({ open: false, org: null }); openModal("reactivate", org); }} />
      <ToastNotification toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default AdministrativeDashboard;